import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Loader2, Check, ArrowLeft, MapPin, Phone, XCircle } from "lucide-react";
import { api, GHS } from "@/lib/api";
import { STATUS_STEPS, STATUS_LABEL, STATUS_COLOR } from "@/lib/status";

export default function OrderDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get(`/orders/${id}`).then((r) => setOrder(r.data)).catch(() => setOrder(false)).finally(() => setLoading(false));
  }, [id]);

  if (loading) return <div className="py-32 flex justify-center"><Loader2 className="w-8 h-8 animate-spin text-brand" /></div>;
  if (!order) return <div className="py-32 text-center text-brand-muted">Order not found.</div>;

  const cancelled = order.status === "cancelled";
  const currentIdx = STATUS_STEPS.findIndex((s) => s.key === order.status);

  return (
    <div className="mx-auto max-w-3xl px-6 py-10">
      <button data-testid="order-back-btn" onClick={() => navigate("/dashboard")} className="flex items-center gap-1.5 text-brand-muted hover:text-brand text-sm mb-8"><ArrowLeft className="w-4 h-4" /> My Orders</button>

      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-display text-3xl text-brand" data-testid="order-id">Order #{order.order_id}</h1>
          <p className="text-sm text-brand-muted mt-1">Placed {new Date(order.created_at).toLocaleString("en-GB")}</p>
        </div>
        <span className={`text-sm font-semibold px-4 py-1.5 rounded-full ${STATUS_COLOR[order.status]}`} data-testid="order-status-badge">{STATUS_LABEL[order.status]}</span>
      </div>

      {/* Tracking timeline */}
      <div className="mt-10 bg-white border border-brand/10 rounded-sm p-7">
        <h2 className="font-display text-xl text-brand mb-6">Order Tracking</h2>
        {cancelled ? (
          <div className="flex items-center gap-3 text-brand-terracotta"><XCircle className="w-6 h-6" /> This order was cancelled.</div>
        ) : (
          <div className="relative" data-testid="tracking-timeline">
            {STATUS_STEPS.map((step, i) => {
              const done = i <= currentIdx;
              const active = i === currentIdx;
              return (
                <div key={step.key} className="flex gap-4 pb-8 last:pb-0 relative">
                  {i < STATUS_STEPS.length - 1 && <div className={`absolute left-[15px] top-8 bottom-0 w-0.5 ${i < currentIdx ? "bg-brand" : "bg-brand/15"}`} />}
                  <div className={`relative z-10 w-8 h-8 rounded-full flex items-center justify-center shrink-0 ${done ? "bg-brand text-white" : "bg-brand-surface text-brand-muted border border-brand/15"}`}>
                    {done ? <Check className="w-4 h-4" /> : <span className="text-xs">{i + 1}</span>}
                  </div>
                  <div className="pt-1">
                    <p className={`font-medium ${done ? "text-brand" : "text-brand-muted"} ${active ? "font-semibold" : ""}`}>{step.label}</p>
                    {active && <p className="text-xs text-brand-terracotta mt-0.5">Current status</p>}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Items */}
      <div className="mt-6 bg-white border border-brand/10 rounded-sm p-7">
        <h2 className="font-display text-xl text-brand mb-4">Items</h2>
        <div className="space-y-4" data-testid="order-items">
          {order.items.map((i) => (
            <div key={i.product_id} className="flex gap-3">
              <img src={i.image} alt={i.name} className="w-14 h-14 object-cover rounded-sm bg-brand-surface" />
              <div className="flex-1"><p className="text-sm font-medium text-brand">{i.name}</p><p className="text-xs text-brand-muted">{i.quantity} × {GHS(i.price)}</p></div>
              <p className="text-sm font-semibold text-brand">{GHS(i.subtotal)}</p>
            </div>
          ))}
        </div>
        <div className="border-t border-brand/10 mt-4 pt-4 flex justify-between font-bold text-brand">
          <span>Total (Cash on Delivery)</span><span data-testid="order-total">{GHS(order.total)}</span>
        </div>
      </div>

      {/* Delivery */}
      <div className="mt-6 bg-white border border-brand/10 rounded-sm p-7">
        <h2 className="font-display text-xl text-brand mb-4">Delivery Details</h2>
        <div className="space-y-2 text-sm text-brand-muted">
          <p className="text-brand font-medium">{order.full_name}</p>
          <p className="flex items-center gap-2"><Phone className="w-4 h-4" /> {order.phone}</p>
          <p className="flex items-start gap-2"><MapPin className="w-4 h-4 mt-0.5" /> {order.address}, {order.city}, {order.region}</p>
          {order.notes && <p className="italic">Note: {order.notes}</p>}
        </div>
      </div>
    </div>
  );
}
